import {
  Modal,
  Button,
  Input,
  Form,
  Typography,
  PhoneNumber,
  Textarea,
} from '@crisellevaldez/speculo-ui';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TeamMemberInput, teamMemberSchema } from '../../../schemas/teamMember.schema';
import { useTeamMemberPhoto } from '../../../hooks/organizations/useTeamMemberPhoto';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useState } from 'react';
import { Upload } from 'lucide-react';

interface EditTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TeamMemberInput) => Promise<void>;
  isSubmitting: boolean;
  initialData: {
    firstName: string;
    lastName?: string;
    phoneNumber: string;
    email?: string;
    bio?: string;
    photoUrl?: string;
    slug?: string;
  };
}

export const EditTeamMemberModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialData,
}: EditTeamMemberModalProps) => {
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { uploadPhoto } = useTeamMemberPhoto();
  const { organization } = useOrganizationStore();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TeamMemberInput>({
    resolver: zodResolver(teamMemberSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: initialData.firstName,
      lastName: initialData.lastName || '',
      phoneNumber: initialData.phoneNumber,
      email: initialData.email || '',
      bio: initialData.bio || '',
      photoUrl: initialData.photoUrl || '',
      slug: initialData.slug || '',
    },
  });

  const onFormSubmit = async (data: TeamMemberInput) => {
    try {
      setFormError(null);
      let finalPhotoUrl = data.photoUrl || '';

      if (selectedPhoto && organization?.id) {
        setIsUploadingPhoto(true);
        try {
          const teamMemberId = initialData.phoneNumber.replace(/\D/g, '') || `edit_${Date.now()}`;
          finalPhotoUrl = await uploadPhoto(
            selectedPhoto,
            organization.id,
            teamMemberId,
            initialData.photoUrl
          );
        } catch (error) {
          console.error('Error uploading photo:', error);
          setFormError('Failed to upload photo');
          setIsUploadingPhoto(false);
          return;
        }
        setIsUploadingPhoto(false);
      }

      await onSubmit({ ...data, photoUrl: finalPhotoUrl });
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error instanceof Error) {
        setFormError(error.message);
      }
    }
  };

  const currentPhotoUrl = watch('photoUrl');

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>
        <Typography variant="h3" className="font-bold">
          Edit Team Member
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-md">
                <p className="text-red-600 dark:text-red-400 text-sm">{formError}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Form.Control>
                <Form.Label required>First name</Form.Label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter first name"
                      error={errors.firstName?.message}
                    />
                  )}
                />
              </Form.Control>

              <Form.Control>
                <Form.Label>Last name</Form.Label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      placeholder="Enter last name"
                      error={errors.lastName?.message}
                    />
                  )}
                />
              </Form.Control>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Control>
                <Form.Label required>Email</Form.Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="email"
                      placeholder="you@example.com"
                      error={errors.email?.message}
                    />
                  )}
                />
              </Form.Control>

              <Form.Control>
                <Form.Label required>Phone number</Form.Label>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <PhoneNumber
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.phoneNumber?.message}
                    />
                  )}
                />
              </Form.Control>
            </div>

            <Form.Control>
              <Form.Label>Bio</Form.Label>
              <Controller
                name="bio"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value || ''}
                    placeholder="Enter team member bio"
                    error={errors.bio?.message}
                    rows={4}
                  />
                )}
              />
            </Form.Control>

            <Form.Control>
              <Form.Label>URL Slug</Form.Label>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ''}
                    placeholder="Enter custom URL slug (optional)"
                    error={errors.slug?.message}
                    helperText="Custom URL for team member profile page. Use lowercase letters, numbers, and hyphens only."
                  />
                )}
              />
            </Form.Control>

            <Form.Control>
              <Form.Label>Photo</Form.Label>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('edit-photo-upload')?.click()}
                    disabled={isUploadingPhoto}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Browse
                  </Button>
                  {selectedPhoto && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedPhoto.name} ({Math.round(selectedPhoto.size / 1024)} KB)
                    </span>
                  )}
                </div>
                {(selectedPhoto || currentPhotoUrl) && (
                  <div className="flex justify-start">
                    <div className="w-24 h-24 rounded-md overflow-hidden bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                      <img
                        src={selectedPhoto ? URL.createObjectURL(selectedPhoto) : currentPhotoUrl}
                        alt="Team member photo preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                <input
                  id="edit-photo-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => setSelectedPhoto(e.target.files?.[0] || null)}
                />
              </div>
            </Form.Control>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting || isUploadingPhoto}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUploadingPhoto}
                isLoading={isSubmitting || isUploadingPhoto}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
